
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";

import { Trash2, Plus, Info, Eye, EyeOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  useSetupMCPServer,
  useTestMcpAuth,
} from "features/chat/agent-chat-module/hooks/use-gpt-chat";
import { useToast } from "hooks/use-toast";
import {
  configParamsDefaultValues,
  createConfigParamsSchema,
  buildConfigParamsFormValuesFromMcp,
  encodeBasicCredentials,
  resolveMcpProbeFromConfig,
} from "./config-params-modal-utils";
import type {
  IConfigParamsFormValues,
  ConfigParamsTransportMethod,
  IMCPAuthProbeResult,
} from "features/chat/agent-chat-module/services/gpt-chat.service";

import { IMCPServerSetupPayload, IToolMcpConfig } from "features/chat/agent-chat-module/services/gpt-chat.service";
import { useQueryClient } from "@tanstack/react-query";
import { ToolAuthCard } from "./tool-auth-card";
import { ConfigParamsAuthTypeSelect } from "./config-params-auth-type-select";
import { ToolConnectionCard } from "./tool-connection-card";

interface ConfigParamsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  toolId: string;
  mcpConfig?: IToolMcpConfig;
  showCard?: boolean;
  isBlocksTool?: boolean;
  projectKey: string;
}

export const ConfigParamsModal = ({
  open,
  onOpenChange,
  name,
  toolId,
  mcpConfig,
  isBlocksTool,
  projectKey
}: ConfigParamsModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: setupMCPServer, isPending } = useSetupMCPServer();
  const { mutateAsync: testMcpAuth, isPending: isTestingAuth } = useTestMcpAuth();

  /** Outcome of the save-time probe (bare request → WWW-Authenticate → RFC 9728/8414/7591). */
  const [probe, setProbe] = useState<IMCPAuthProbeResult | null>(null);

  const form = useForm<IConfigParamsFormValues>({
    defaultValues: configParamsDefaultValues,
    resolver: zodResolver(createConfigParamsSchema(t)),
    mode: "onChange",
  });

  const initializedToolIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initializedToolIdRef.current = null;
      return;
    }

    setProbe(resolveMcpProbeFromConfig(mcpConfig?.AuthConfig));

    // Init once per open tool — re-running form.reset on every mcpConfig refetch
    // wipes keystrokes and steals focus from the inputs being edited.
    if (initializedToolIdRef.current === toolId) return;
    initializedToolIdRef.current = toolId;

    form.reset(mcpConfig ? buildConfigParamsFormValuesFromMcp(mcpConfig) : configParamsDefaultValues);
  }, [open, toolId, mcpConfig, form]);

  const transportMethod =
    useWatch({ control: form.control, name: "transportMethod" }) ??
    configParamsDefaultValues.transportMethod;
  const trustMcpServer = useWatch({ control: form.control, name: "trustMcpServer" }) ?? false;
  const authType =
    useWatch({ control: form.control, name: "authType" }) ?? configParamsDefaultValues.authType;
  const tokenAsCookie = useWatch({ control: form.control, name: "tokenAsCookie" }) ?? false;
  const headers =
    useWatch({ control: form.control, name: "headers" }) ?? configParamsDefaultValues.headers;
  const queryParams =
    useWatch({ control: form.control, name: "queryParams" }) ??
    configParamsDefaultValues.queryParams;

  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showClientSecretPerUser, setShowClientSecretPerUser] = useState(false);

  const addHeader = () => {
    const currentHeaders = form.getValues("headers");
    form.setValue("headers", [...currentHeaders, { key: "", value: "" }]);
  };

  const removeHeader = (index: number) => {
    const currentHeaders = form.getValues("headers");
    form.setValue(
      "headers",
      currentHeaders.filter((_, i) => i !== index),
    );
  };

  const updateHeader = (index: number, field: "key" | "value", value: string) => {
    const currentHeaders = form.getValues("headers");
    const newHeaders = [...currentHeaders];
    newHeaders[index][field] = value;
    form.setValue("headers", newHeaders);
  };

  const addQueryParam = () => {
    const currentParams = form.getValues("queryParams");
    form.setValue("queryParams", [...currentParams, { key: "", value: "" }]);
  };

  const removeQueryParam = (index: number) => {
    const currentParams = form.getValues("queryParams");
    form.setValue(
      "queryParams",
      currentParams.filter((_, i) => i !== index),
    );
  };

  const updateQueryParam = (index: number, field: "key" | "value", value: string) => {
    const currentParams = form.getValues("queryParams");
    const newQueryParams = [...currentParams];
    newQueryParams[index][field] = value;
    form.setValue("queryParams", newQueryParams);
  };

  const handleTestAuthentication = async () => {
    try {
      const authType = form.watch("authType");
      let oauth2Config: Record<string, unknown> = {};

      if (authType === "bearer") {
        oauth2Config = {
          APIToken: form.watch("apiToken"),
          APIKeyPrefix: "Bearer",
          APIKeyName: "Authorization",
        };
      } else if (authType === "oauth2_client_credentials") {
        oauth2Config = {
          ClientID: form.watch("clientId"),
          ClientSecret: form.watch("clientSecret"),
          TokenURL: form.watch("tokenUrl"),
          Scopes: (form.watch("scopes") || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: "client_credentials",
        };
      }

      const result = await testMcpAuth({
        toolId,
        projectKey: projectKey,
        payload: {
          auth_config: oauth2Config,
        },
        isBlocksTool
      });

      if (result.is_success) {
        toast({
          variant: 'success',
          title: t("SUCCESS", "Success"),
          description: t("AUTHENTICATION_TEST_SUCCESSFUL", "Authentication test successful"),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("FAILED", "Failed"),
          description: result.message || t("AUTHENTICATION_TEST_FAILED", "Authentication test failed"),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("FAILED", "Failed"),
        description:
          error instanceof Error
            ? error.message
            : t("AUTHENTICATION_TEST_FAILED", "Authentication test failed"),
      });
    }
  };

  const handleSave = form.handleSubmit(async (values) => {
    try {
      const headersObj: Record<string, string> = {};
      values.headers.forEach((h) => {
        if (h.key && h.value) {
          headersObj[h.key] = h.value;
        }
      });

      const queryParamsObj: Record<string, string> = {};
      values.queryParams.forEach((q) => {
        if (q.key && q.value) {
          queryParamsObj[q.key] = q.value;
        }
      });

      const authConfig: IMCPServerSetupPayload["auth_config"] = {
        Enabled: values.authType !== "none",
        AutoDiscoverMetadata: false,
      };

      if (values.authType === "bearer") {
        authConfig.APIToken = values.apiToken || "";
        authConfig.APIKeyName = values.authKeyName || "Authorization";
        authConfig.APIKeyPrefix = values.authKeyPrefix ?? "Bearer";
      } else if (values.authType === "basic") {
        authConfig.APIToken = encodeBasicCredentials(values.username, values.password);
        authConfig.APIKeyName = "Authorization";
        authConfig.APIKeyPrefix = "Basic";
      } else if (values.authType === "api_token") {
        authConfig.APIToken = values.apiToken || "";
        authConfig.APIKeyName = values.authKeyName || "X-API-Key";
        authConfig.APIKeyPrefix = values.authKeyPrefix || "";
      } else if (values.authType === "oauth2_client_credentials") {
        authConfig.OAuth2Config = {
          ClientID: values.clientId || "",
          ClientSecret: values.clientSecret || "",
          TokenURL: values.tokenUrl || "",
          Scopes: (values.scopes || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          GrantType: "client_credentials",
        };
      } else if (values.authType === "oauth2_per_user") {
        // URL-only: the server probes on save and fills in the endpoints + client.
        // A typed client is the fallback when dynamic registration is unavailable.
        authConfig.AuthMode = "per_user";
        if (values.clientIdPerUser) {
          authConfig.OAuth2Config = {
            ClientID: values.clientIdPerUser,
            ClientSecret: values.clientSecretPerUser || "",
            TokenURL: "",
            Scopes: [],
            GrantType: "authorization_code",
          };
        }
      } else if (values.authType === "host_session") {
        authConfig.AcceptHostToken = true;
        authConfig.TokenAsCookie = !!values.tokenAsCookie;
        if (values.tokenAsCookie) {
          authConfig.TokenCookieName = values.tokenCookieName || "ACCESS_TOKEN";
        } else {
          authConfig.APIKeyName = "Authorization";
          authConfig.APIKeyPrefix = "Bearer";
        }
      }

      const payload: IMCPServerSetupPayload = {
        server_url: values.serverUrl,
        transport: values.transportMethod,
        headers: headersObj,
        query_params: queryParamsObj,
        trust_mcp_server: values.trustMcpServer,
        auth_config: authConfig,
        project_key: projectKey,
        is_open_tool: isBlocksTool,
      };

      const result = await setupMCPServer({ toolId, payload, projectKey: projectKey });

      if (result.is_success) {
        const mcpProbe = result.data?.mcpProbe;

        if (mcpProbe) {
          // Keep the dialog open: the admin still has to sign in (or type a client).
          setProbe(mcpProbe);
          if (mcpProbe.status === "none") form.setValue("authType", "none");
          toast({
            variant: 'success',
            title: t("SAVED", "Saved"),
            description:
              mcpProbe.detail ||
              t("MCP_SERVER_CONFIGURED_SUCCESSFULLY",
              "MCP Server configured successfully"),
          });
          queryClient.invalidateQueries({ queryKey: ["tools", "detail", toolId, projectKey] });
          return;
        }

        toast({
          variant: 'success',
          title: t("SUCCESS", "Success"),
          description: t("MCP_SERVER_CONFIGURED_SUCCESSFULLY", "MCP Server configured successfully"),
        });
        queryClient.invalidateQueries({ queryKey: ["tools", "detail", toolId, projectKey] });
        form.reset();
        onOpenChange(false);
      } else {
        toast({
          variant: "destructive",
          title: t("FAILED", "Failed"),
          description: result.message || t("FAILED_TO_CONFIGURE_MCP_SERVER", "Failed to configure MCP server"),
        });
      }
    } catch (error) {
      console.error("Failed to setup MCP server:", error);
      toast({
        variant: "destructive",
        title: t("FAILED", "Failed"),
        description:
          error instanceof Error ? error.message : t("FAILED_TO_SETUP_MCP_SERVER", "Failed to setup MCP server"),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden p-0"
        overlayClassName="backdrop-blur-none"
      >
        <DialogHeader className="shrink-0 px-5 pt-5">
          <DialogTitle className="text-lg font-semibold">
            {t("CONFIG_PARAMS", "Configure params")}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 touch-pan-y">
              <div className="space-y-6">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <Label className="mb-2">{t("METHOD", "Method")}</Label>
                <RadioGroup
                  value={transportMethod}
                  onValueChange={(value) =>
                    form.setValue("transportMethod", value as ConfigParamsTransportMethod)
                  }
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="sse" id="sse" />
                      <Label htmlFor="sse" className="font-normal">
                        SSE
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="streamable_http" id="streamable" />
                      <Label htmlFor="streamable" className="font-normal">
                        {t("STREAMABLE_HTTP", "Streamable HTTP")}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="serverUrl" className="mb-2">
                  {t("URL", "URL")} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="serverUrl"
                  type="url"
                  placeholder={t("ENTER_URL", "Enter url")}
                  {...form.register("serverUrl")}
                  className="w-full"
                />
              </div>
              {form.formState.errors.serverUrl && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.serverUrl.message}
                </p>
              )}
            </div>

            <Card>
              <CardContent>
                <div className="flex flex-col gap-6">
                  <ConfigParamsAuthTypeSelect />

                  {(authType === "bearer" || authType === "basic" || authType === "api_token") && (
                    <ToolAuthCard
                      authType={authType}
                      showAuthTypeSelector={false}
                      excludeAuthTypes={["oauth2"]}
                      showCard={false}
                    />
                  )}

                  {/* OAuth 2.1 Client Credentials Card */}
                  {authType === "oauth2_client_credentials" && (
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-2">
                          <label className="font-medium">{t("CONFIGURATION", "Configuration")}</label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {t("CONFIGURE_OAUTH2_CLIENT_CREDENTIALS",
                                    "Configure OAuth 2.1 Client Credentials authentication")}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientId" className="mb-2">
                              {t("CLIENT_ID", "Client ID")} <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="clientId"
                              placeholder={t("ENTER_CLIENT_ID", "Enter client ID")}
                              {...form.register("clientId")}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="clientSecret" className="mb-2">
                              {t("CLIENT_SECRET", "Client Secret")}{" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <div className="relative">
                              <Input
                                id="clientSecret"
                                type={showClientSecret ? "text" : "password"}
                                placeholder={t("ENTER_CLIENT_SECRET", "Enter client secret")}
                                {...form.register("clientSecret")}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowClientSecret(!showClientSecret)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showClientSecret ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="tokenUrl" className="mb-2">
                              {t("TOKEN_URL", "Token URL")} <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="tokenUrl"
                              type="url"
                              placeholder="https://example.com/oauth/token"
                              {...form.register("tokenUrl")}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="scopes" className="mb-2">
                              {t("SCOPES_COMMA_SEPARATED", "Scopes (comma-separated)")}
                            </Label>
                            <Input
                              id="scopes"
                              placeholder={t("SCOPES_PLACEHOLDER", "read, write")}
                              {...form.register("scopes")}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          onClick={handleTestAuthentication}
                          className="mt-3 w-fit"
                          disabled={isTestingAuth}
                        >
                          {isTestingAuth
                            ? t("TESTING", "Testing...")
                            : t("TEST_AUTHENTICATION", "Test Authentication")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* OAuth 2.1 — each user signs in */}
                  {authType === "oauth2_per_user" && (
                    <div className="flex flex-col gap-4">
                      <div className="rounded-lg bg-muted/50 p-4">
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-2">
                            <label className="font-medium">
                              {t("OAUTH21_PER_USER", "OAuth 2.1 - Each user signs in")}
                            </label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">
                                    {t("MCP_PER_USER_OAUTH_TOOLTIP",
                                      "Save with the URL only. We ask the server how it authenticates, discover its authorization server and register a client automatically. Each user then connects their own account.")}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>

                          {probe ? (
                            <div
                              className={
                                probe.status === "per_user"
                                  ? "rounded-md bg-green-100 px-3 py-2 text-sm text-green-800"
                                  : probe.status === "none"
                                    ? "rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
                                    : probe.status === "failed"
                                      ? "rounded-md bg-red-100 px-3 py-2 text-sm text-red-800"
                                      : "rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-800"
                              }
                            >
                              {probe.status === "per_user"
                                ? t("MCP_PROBE_PER_USER_DETECTED", "Per-user sign-in detected.")
                                : probe.status === "none"
                                  ? t("MCP_PROBE_NO_AUTH_NEEDED",
                                    "This server needs no authentication.")
                                  : probe.status === "failed"
                                    ? t("MCP_PROBE_FAILED",
                                      "Could not reach this server to detect its authentication. Check the URL and try saving again.")
                                    : t("MCP_PROBE_MANUAL_CLIENT_REQUIRED",
                                      "This server does not register clients automatically. Enter a Client ID below.")}{" "}
                              {probe.detail}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {t("MCP_PROBE_SAVE_TO_DETECT",
                                "Save to detect how this server authenticates.")}
                            </p>
                          )}

                          <div className="flex flex-col gap-2">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="clientIdPerUser" className="mb-2">
                                  {t("CLIENT_ID", "Client ID")}{" "}
                                  {probe?.needsManualClient && (
                                    <span className="text-destructive">*</span>
                                  )}
                                </Label>
                                <Input
                                  id="clientIdPerUser"
                                  placeholder={
                                    t("MCP_MANUAL_CLIENT_ID_PLACEHOLDER",
                                    "Only needed if the server has no dynamic registration")
                                  }
                                  {...form.register("clientIdPerUser")}
                                />
                              </div>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="clientSecretPerUser" className="mb-2">
                                  {t("CLIENT_SECRET", "Client Secret")}
                                </Label>
                                <div className="relative">
                                  <Input
                                    id="clientSecretPerUser"
                                    type={showClientSecretPerUser ? "text" : "password"}
                                    placeholder={
                                      t("MCP_MANUAL_CLIENT_SECRET_PLACEHOLDER",
                                      "Leave empty for a public client")
                                    }
                                    {...form.register("clientSecretPerUser")}
                                    className="pr-10"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowClientSecretPerUser(!showClientSecretPerUser)
                                    }
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  >
                                    {showClientSecretPerUser ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {probe?.registered && (
                              <p className="text-xs text-muted-foreground">
                                {t("MCP_CLIENT_REPLACES_REGISTERED_HINT",
                                  "A client entered here replaces the one registered automatically with this server.")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {probe?.status === "per_user" && !probe.needsManualClient && (
                        <ToolConnectionCard toolId={toolId} toolName={name} subType="oidc" />
                      )}
                    </div>
                  )}

                  {/* Host session (embedded widget) */}
                  {authType === "host_session" && (
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3 rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
                          <Info className="h-6 w-6 min-w-[1.5rem] text-yellow-600" />
                          <span>
                            <strong>
                              {t("MCP_HOST_SESSION_WARNING_TITLE",
                                "Only for MCP servers you own.")}
                            </strong>{" "}
                            {t("MCP_HOST_SESSION_WARNING",
                              "Each call is made with the signed-in visitor's session token from the site that embeds the widget. A third-party MCP server will not accept the embedding site's token and will reject every call. Outside the widget on that site (console tests, platform chat, scheduled runs) this tool does not run.")}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="mcpTokenAsCookie"
                            checked={tokenAsCookie}
                            onCheckedChange={(checked) =>
                              form.setValue("tokenAsCookie", checked as boolean, {
                                shouldDirty: true,
                              })
                            }
                          />
                          <Label htmlFor="mcpTokenAsCookie" className="cursor-pointer font-medium">
                            {t("SEND_TOKEN_AS_COOKIE", "Send token as a cookie")}
                          </Label>
                        </div>
                        <p className="-mt-2 text-xs text-muted-foreground">
                          {t("MCP_SEND_TOKEN_AS_COOKIE_HINT",
                            "For cookie-gated servers: the token is replayed verbatim as a session cookie instead of an Authorization: Bearer header.")}
                        </p>

                        {tokenAsCookie && (
                          <div className="flex w-full flex-col gap-2 sm:w-1/2">
                            <Label htmlFor="mcpTokenCookieName" className="mb-2">
                              {t("TOKEN_COOKIE_NAME", "Cookie name")}
                            </Label>
                            <Input
                              id="mcpTokenCookieName"
                              placeholder="ACCESS_TOKEN"
                              {...form.register("tokenCookieName")}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="flex flex-col gap-6">
                  <div className="mb-2 flex items-center gap-3 rounded-md bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
                    <Info className="h-6 w-6 min-w-[1.5rem] text-yellow-600" />
                    <span>
                      <strong>{t("CAUTION", "Caution")}:</strong>{" "}
                      {t("MCP_HEADERS_SECURITY_WARNING",
                        "Sending sensitive data (like tokens, credentials, or secrets) via HTTP headers can expose them to security risks. Only include values you trust and avoid sharing confidential information unless absolutely necessary.")}
                    </span>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="font-bold">{t("HEADERS", "Headers")}</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("MCP_HEADERS_TOOLTIP", "Set custom headers for your MCP server requests")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-[1fr_1fr_40px] gap-4 font-medium">
                        <Label>{t("KEY", "Key")}</Label>
                        <Label>{t("VALUE", "Value")}</Label>
                        <div></div>
                      </div>

                      {headers.map((header, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[1fr_1fr_40px] items-center gap-4"
                        >
                          <Input
                            placeholder={t("HEADER_KEY", "Header Key")}
                            value={header.key}
                            onChange={(e) => updateHeader(index, "key", e.target.value)}
                          />
                          <Input
                            placeholder={t("ENTER_VALUE", "Enter value")}
                            value={header.value}
                            onChange={(e) => updateHeader(index, "value", e.target.value)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeHeader(index)}
                            className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                            disabled={headers.length === 1}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addHeader}
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {t("ADD", "Add")}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="font-bold">{t("QUERY_PARAMS", "Query Params")}</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {t("MCP_QUERY_PARAMS_TOOLTIP",
                                "Set query parameters for your MCP server requests")}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-[1fr_1fr_40px] gap-4 font-medium">
                        <Label>{t("KEY", "Key")}</Label>
                        <Label>{t("VALUE", "Value")}</Label>
                        <div></div>
                      </div>

                      {queryParams.map((param, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-[1fr_1fr_40px] items-center gap-4"
                        >
                          <Input
                            placeholder={t("QUERY_KEY", "Query Key")}
                            value={param.key}
                            onChange={(e) => updateQueryParam(index, "key", e.target.value)}
                          />
                          <Input
                            placeholder={t("ENTER_VALUE", "Enter value")}
                            value={param.value}
                            onChange={(e) => updateQueryParam(index, "value", e.target.value)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeQueryParam(index)}
                            className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                            disabled={queryParams.length === 1}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addQueryParam}
                        className="w-fit gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {t("ADD", "Add")}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="trustMcp"
                      checked={trustMcpServer}
                      onCheckedChange={(checked) =>
                        form.setValue("trustMcpServer", checked as boolean)
                      }
                    />
                    <Label htmlFor="trustMcp" className="cursor-pointer font-medium">
                      {t("I_TRUST_MCP_SERVICE", "I trust this MCP Service")}
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t px-5 py-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t("CANCEL", "Cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isPending || !form.formState.isValid || !trustMcpServer}
              >
                {isPending ? t("SAVING", "Saving...") : t("SAVE", "Save")}
              </Button>
            </div>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
